/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  formulaControlValidator,
} from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  FormStateSchema,
  isRouteForCreationMode,
  NumberTypes,
  UserContext,
  whenCreateOrEditMode,
  whenUsRegion,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode, getControl, getControlValue, UploadContext, UploadSlot } from '@gh/core-ui';
import { ItemDetails } from '../../resources/component-and-item-details.model';
import { ComponentOrItemFormState } from '../component-or-item-form-state';

@Injectable()
export class ItemDetailsFormState extends ComponentOrItemFormState<ItemDetails> {
  pictureSlot: UploadSlot;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private uploadContext: UploadContext,
              schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);

    this.pictureSlot = uploadContext.createUploadSlot({
      entryPoint: 'ITEM',
    });
  }

  get id(): ID {
    return getControlValue(this.group, 'id');
  }

  get unitOfMeasureId(): ID {
    return getControlValue(this.group, 'unitsOfMeasureId');
  }

  build(item: ItemDetails): void {
    this.initSchema(item);

    this.pictureSlot.setControl(getControl(this.group, 'picture'));
  }

  isFormulaInvalid(): boolean {
    return getControl(this.group, 'formula').invalid;
  }

  protected buildSchema(item: ItemDetails, { root }: FormStateSchema): FormRootSchema {
    const locked = item.editLocked;

    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode : (this.userContext.hasPermission(
      BuilderPermission.Setup.Estimating.Items.EDIT) ? FormMode.EditMode : FormMode.ViewMode);

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        code: { required: true, enabled: !locked },
        name: { required: true, enabled: !locked },
        cost: { required: true, type: NumberTypes.SignedPrice },
        unitsOfMeasureId: { enabled: !locked },
        isSalesTax: { visible: whenUsRegion() },
        version: void 0,
        formula: { enabled: !locked, validators: [formulaControlValidator] },
        picture: { asyncValidators: [this.pictureSlot.formValidator()] },
        memo: { enabled: !locked },
        isFormulaNeedPropagation: void 0,
        oldFormula: void 0,
        active: void 0,
      },
    });
  }
}
